import { Action, Reducer } from 'redux';
import { AppThunkAction } from '.';
import axios, { AxiosError } from 'axios';
import { receiveAxiosError } from '@konicaminolta/common-react';
import { Dashboard } from '../types/Dashboard/Dashboard';
import { REQUEST_DASHBOARDS, RECEIVE_DASHBOARDS, REQUEST_DASHBOARD_DETAILS, RECEIVE_DASHBOARD_DETAILS, UNLOAD_DASHBOARD_DETAILS, DashboardActionTypes, REQUEST_DASHBOARD_EMBEDDED_VIEW, RECEIVE_DASHBOARD_EMBEDDED_VIEW, UNLOAD_DASHBOARD_EMBEDDED_VIEW } from '../types/actions';
import { CALL_HISTORY_METHOD } from 'connected-react-router';
import { EmbeddedRecordAccess } from '../types/Common/EmbeddedRecordAccess';
import { PowerBiObjectType } from '../types/Common/PowerBiObjectType';

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface DashboardState {
    data: Dashboard[];
    embeddedDashboardDetailsData: EmbeddedRecordAccess[];
    isLoadingData: boolean;
    isLoadedData: boolean;
    hasPermissionsToList: boolean;
    isLoadingDetails: boolean;
    isLoadedDetails: boolean;
    hasPermissionsToDetails: boolean;
    isLoadingEmbeddedViewDetails: boolean;
    isLoadedEmbeddedViewDetails: boolean;
}

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const actionCreators = {
    requestDashboards: (): AppThunkAction<DashboardActionTypes> => (dispatch, getState) => {

        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();

        if (appState 
            && appState.dashboard !== undefined 
            && !appState.dashboard.isLoadingData 
            && !appState.dashboard.isLoadedData) 
        {
            dispatch({ type: REQUEST_DASHBOARDS });

            axios
                .request<Dashboard[]>({
                    url: `./api/dashboards`
                })
                .then((response: { data: Dashboard[]; }) => {
                    const { data } = response;
                    dispatch({ type: RECEIVE_DASHBOARDS, data, hasPermissionsToList: true });
                })
                .catch((e: AxiosError) => {

                    if (e.response && e.response.status == 401) {
                        dispatch({ type: RECEIVE_DASHBOARDS, data: [], hasPermissionsToList: false });
                        return;
                    }

                    dispatch(receiveAxiosError({error: e}));
                });
        }
    },

    requestDashboardDetails: (dashboardId: string): AppThunkAction<DashboardActionTypes> => (dispatch, getState) => {
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();

        if (appState 
            && appState.dashboard !== undefined 
            && !appState.dashboard.isLoadingDetails 
            && !appState.dashboard.isLoadedDetails) 
        {

            dispatch({ type: REQUEST_DASHBOARD_DETAILS });

            axios
                .request<Dashboard>({
                    url: `./api/dashboards/${dashboardId}`
                })
                .then((response: { data: Dashboard; }) => {
                    const { data } = response;
                    dispatch({ type: RECEIVE_DASHBOARD_DETAILS, dashboard: data, hasPermissionsToDetails: true });
                })
                .catch((e: AxiosError) => {
                    if (e.response && e.response.status == 401) {
                        dispatch({ type: RECEIVE_DASHBOARD_DETAILS, hasPermissionsToDetails: false });
                        return;
                    }

                    dispatch(receiveAxiosError({error: e}));
                    dispatch({ type: RECEIVE_DASHBOARD_DETAILS, hasPermissionsToDetails: true });
                    // if a specific resource is not found redirect to resource index page
                    if(e.response && e.response.status >= 400 && e.response.status < 500){
                        dispatch(
                            { 
                                type: CALL_HISTORY_METHOD, 
                                payload: { method: 'push', 'args': [ `/dashboards` ] }
                            }
                        );
                    }
                });

        }
    },

    requestDashboardMinimalDetails: (dashboardId: string): AppThunkAction<DashboardActionTypes> => (dispatch, getState) => {
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();

        if (appState 
            && appState.dashboard !== undefined 
            && !appState.dashboard.isLoadingDetails 
            && !appState.dashboard.isLoadedDetails) 
        {

            dispatch({ type: REQUEST_DASHBOARD_DETAILS });

            axios
                .request<Dashboard>({
                    url: `./api/dashboards/viewdashboard/${dashboardId}/details`
                })
                .then((response: { data: Dashboard; }) => {
                    const { data } = response;
                    dispatch({ type: RECEIVE_DASHBOARD_DETAILS, dashboard: data, hasPermissionsToDetails: true });
                })
                .catch((e: AxiosError) => {
                    dispatch(receiveAxiosError({error: e}));

                    if(e.response && e.response.status == 401) {
                        dispatch({ type: RECEIVE_DASHBOARD_DETAILS, hasPermissionsToDetails: false });
                    }
                    else{
                        dispatch({ type: RECEIVE_DASHBOARD_DETAILS, hasPermissionsToDetails: true });
                    }
                    
                    // if a specific resource is not found redirect to resource index page
                    if(e.response && e.response.status >= 400 && e.response.status < 500){
                        dispatch(
                            { 
                                type: CALL_HISTORY_METHOD, 
                                payload: { method: 'push', 'args': [ `/dashboards` ] }
                            }
                        );
                    }
                });

        }
    },

    unloadDashboardDetails: (): AppThunkAction<DashboardActionTypes> => (dispatch) => {

        dispatch({ type: UNLOAD_DASHBOARD_DETAILS });
    },

    requestDashboardEmbeddedViewDetails: (dashboardId: string): AppThunkAction<DashboardActionTypes> => (dispatch, getState) => {
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();

        if (appState 
            && appState.dashboard !== undefined 
            && !appState.dashboard.isLoadingEmbeddedViewDetails 
            && !appState.dashboard.isLoadedEmbeddedViewDetails) 
        {
            let embeddedDashboardDetailViewData = appState.dashboard.embeddedDashboardDetailsData.find(x => x.recordId === dashboardId);

            if(embeddedDashboardDetailViewData === undefined){
                dispatch({ type: REQUEST_DASHBOARD_EMBEDDED_VIEW });

                axios
                    .request<EmbeddedRecordAccess>({
                        url: `./api/dashboards/viewdashboard/${dashboardId}`
                    })
                    .then((response: { data: EmbeddedRecordAccess; }) => {
                        const { data } = response;
                        dispatch({ type: RECEIVE_DASHBOARD_EMBEDDED_VIEW, data: data });
                    })
                    .catch((e: AxiosError) => {
                        if (e.response && e.response.status == 401) {
                            dispatch({ type: RECEIVE_DASHBOARD_EMBEDDED_VIEW });
                            return;
                        }

                        dispatch(receiveAxiosError({error: e}));
                        dispatch({ type: RECEIVE_DASHBOARD_EMBEDDED_VIEW });

                        // if a specific resource is not found redirect to resource index page
                        if(e.response && e.response.status >= 400 && e.response.status < 500){
                            dispatch(
                                { 
                                    type: CALL_HISTORY_METHOD, 
                                    payload: { method: 'push', 'args': [ `/dashboards` ] }
                                }
                            );
                        }
                    });
            }
            else{
                dispatch({ type: RECEIVE_DASHBOARD_EMBEDDED_VIEW });
            }
        }
    },

    unloadDashboardEmbeddedViewDetails: (): AppThunkAction<DashboardActionTypes> => (dispatch) => {
        dispatch({ type: UNLOAD_DASHBOARD_EMBEDDED_VIEW });
    },

};

export const reducer: Reducer<DashboardState> = (state: DashboardState | undefined, incomingAction: Action): DashboardState => {
    if (state === undefined) {
        return {
            data: [],
            embeddedDashboardDetailsData: [],
            isLoadingData: false,
            isLoadedData: false,
            hasPermissionsToList: false,
            isLoadingDetails: false,
            isLoadedDetails: false,
            hasPermissionsToDetails: true,
            isLoadedEmbeddedViewDetails: false,
            isLoadingEmbeddedViewDetails: false,
        };
    }
    const action = incomingAction as DashboardActionTypes;

    switch (action.type) {
        case REQUEST_DASHBOARDS: {
            return {
                ...state,
                isLoadingData: true,
                isLoadedData: false,
            };
        }
        case RECEIVE_DASHBOARDS: {
            return {
                ...state,
                isLoadingData: false,
                isLoadedData: true,
                data: action.data,
                hasPermissionsToList: action.hasPermissionsToList,
            };
        }
        case REQUEST_DASHBOARD_DETAILS: {
            return {
                ...state,
                isLoadingDetails: true,
                isLoadedDetails: false,
            };
        }
        case RECEIVE_DASHBOARD_DETAILS: {
            // spread operator creates a shallow copy which will work for this specific transform of the state
            // since no nested properties are being affected
            let hasPermissionsToDetails = action.hasPermissionsToDetails;
            let dashboards = [...state.data];
            if(action.dashboard !== undefined) {
                let dashboardIndex = dashboards.findIndex(x => x.id == action.dashboard?.id);
                action.dashboard.type = PowerBiObjectType.Dashboard;
                if (dashboardIndex !== -1) {
                    dashboards[dashboardIndex] = action.dashboard;
                }
                else {
                    dashboards.push(action.dashboard);
                }
            }

            return {
                ...state,
                isLoadingDetails: false,
                isLoadedDetails: true,
                data: dashboards,
                hasPermissionsToDetails: hasPermissionsToDetails,
            };
        }
        case UNLOAD_DASHBOARD_DETAILS: {
            return {
                ...state,
                isLoadingDetails: false,
                isLoadedDetails: false,
            };
        }
        case REQUEST_DASHBOARD_EMBEDDED_VIEW: {
            return {
                ...state,
                isLoadingEmbeddedViewDetails: true,
                isLoadedEmbeddedViewDetails: false,
            };
        }
        case RECEIVE_DASHBOARD_EMBEDDED_VIEW: {
            let embededDashboardDetail = [...state.embeddedDashboardDetailsData];
            if(action.data !== undefined){
                embededDashboardDetail.push(action.data);
            }
            return {
                ...state,
                isLoadingEmbeddedViewDetails: false,
                isLoadedEmbeddedViewDetails: true,
                embeddedDashboardDetailsData: embededDashboardDetail,
            };
        }
        case UNLOAD_DASHBOARD_EMBEDDED_VIEW: {
            return {
                ...state,
                isLoadedEmbeddedViewDetails: false,
                isLoadingEmbeddedViewDetails: false,
            };
        }
    }

    return state;
};
